import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Settings as SettingsIcon, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@repo/ui";
import { SettingsTabs } from "@/components/settings/settings-tabs";
import { TrialBanner } from "@/components/settings/trial-banner";
import { useAuth } from "@/lib/auth/auth-provider";
import { usePermissionStore } from "@/lib/stores/permission-store";
import { getPharmacySettings } from "@/lib/api/pharmacy";
import { logger } from "@/lib/utils/logger";

interface PharmacyData {
  id: string;
  legal_name: string;
  dba_name: string | null;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  phone_number: string;
  npi: string;
  ncpdp_id: string | null;
  dea_registration_number: string | null;
  dea_expiration_date: string | null;
  state_license_number: string;
  license_expiration_date: string;
  timezone: string;
  pharmacy_type: string;
  subscription_status: string;
  subscription_started_at: string | null;
  subscription_ends_at: string | null;
  trial_ends_at: string | null;
  stripe_customer_id: string | null;
}

function isPharmacyData(value: unknown): value is PharmacyData {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof (value as Record<string, unknown>).id === "string" &&
    "legal_name" in value
  );
}

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const auth = useAuth();
  const check = usePermissionStore((s) => s.check);
  const subscriptionResult = check("settings:subscription");
  const canManageSubscription =
    subscriptionResult !== null && subscriptionResult.type !== "denied";

  const [pharmacy, setPharmacy] = useState<PharmacyData | null>(null);
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const user = auth.user;

  const fetchPharmacySettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setHasError(false);
      const data = await getPharmacySettings();

      if (isPharmacyData(data)) {
        setPharmacy(data);
      } else {
        console.error("Unexpected pharmacy settings shape:", data);
        setPharmacy(null);
        setHasError(true);
      }
    } catch (err) {
      logger.error("Fetch pharmacy settings failed", err, {
        operation: "fetch_pharmacy_settings",
      });
      setPharmacy(null);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPharmacySettings();
  }, [fetchPharmacySettings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (hasError || !pharmacy) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">
          Failed to load settings. Please try again.
        </p>
        <Button variant="outline" size="sm" onClick={fetchPharmacySettings}>
          Retry
        </Button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
      </div>
      {canManageSubscription && (
        <TrialBanner
          pharmacy={pharmacy}
          onViewPlans={() => setActiveTab("subscription")}
        />
      )}
      <SettingsTabs
        pharmacy={pharmacy}
        user={user}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onPharmacyUpdate={fetchPharmacySettings}
      />
    </div>
  );
}
